import axios, { AxiosError } from "axios";
import { Campaign, CampaignStats } from "../types/campaign";
import { logError, logInfo } from "./monitoring";
import { API_URL } from "../config";

// Use the API_URL from config instead of hardcoding
// Force rebuild to update API URL - timestamp: 2025-05-30
const getApiBaseUrl = () => {
  return API_URL;
};

const apiBaseUrl = getApiBaseUrl();
logInfo(`API Base URL: ${apiBaseUrl}`);

// Use an enhanced campaign type that includes product, subject, body properties
interface EnhancedCampaign extends Campaign {
  product?: string;
  subject?: string;
  body?: string;
  type?: string;
  sentDate?: string;
}

// Enhanced retry logic
const MAX_RETRIES = 3;
const RETRY_DELAY = 800; // ms
const RETRY_BACKOFF_FACTOR = 1.5;

// Add this custom helper for retrying requests with exponential backoff
const retryRequest = async (fn, retries = MAX_RETRIES, delay = RETRY_DELAY) => {
  try {
    return await fn();
  } catch (error) {
    if (retries <= 0) {
      throw error;
    }

    // Retry for network errors, 5xx responses, and specific 4xx errors that might be temporary
    if (!error.response || 
        error.response.status >= 500 || 
        error.response.status === 429 || // Too many requests
        error.response.status === 408) { // Request timeout
      
      logInfo(
        `Retrying request after error: ${error.message} (${retries} retries left, delay: ${delay}ms)`
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
      return retryRequest(fn, retries - 1, Math.floor(delay * RETRY_BACKOFF_FACTOR));
    }

    throw error;
  }
};

// Helper function to get headers with authentication
const getHeaders = () => {
  const token = localStorage.getItem("auth_token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

// Create a consistent api instance
export const api = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000, // 30 second timeout for API requests
});

// Add request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  const timestamp = new Date().toISOString();

  console.log("🔵 API Request:", {
    method: config.method?.toUpperCase(),
    url: config.url,
    baseURL: config.baseURL,
    fullUrl: `${config.baseURL}${config.url}`,
    hasToken: !!token,
    tokenLength: token?.length || 0,
    headers: config.headers,
    data: config.data,
    timestamp,
  });

  if (token) {
    // Ensure we're using Bearer format for the token
    config.headers.Authorization = `Bearer ${token}`;

    // Add token debugging
    logInfo(
      `Adding auth token to ${config.url} request (length: ${token.length})`
    );
    console.log("✅ API Request: Auth token added");
  } else {
    logInfo(`No auth token found for ${config.url} request`);
    console.log("⚠️ API Request: No auth token found");
  }
  return config;
});

// Enhance existing api interceptors to handle common errors more gracefully
api.interceptors.response.use(
  (response) => {
    const timestamp = new Date().toISOString();

    console.log("✅ API Response:", {
      method: response.config.method?.toUpperCase(),
      url: response.config.url,
      status: response.status,
      statusText: response.statusText,
      dataType: typeof response.data,
      dataKeys:
        response.data && typeof response.data === "object"
          ? Object.keys(response.data)
          : "not object",
      dataLength: Array.isArray(response.data)
        ? response.data.length
        : "not array",
      hasSuccess:
        response.data?.success !== undefined
          ? response.data.success
          : "no success field",
      hasLeads:
        response.data?.leads !== undefined
          ? `${response.data.leads?.length || 0} leads`
          : "no leads field",
      timestamp,
    });

    // Log detailed response for leads endpoint
    if (response.config.url?.includes("/leads")) {
      console.log("🔍 API Response (Leads Detail):", {
        fullResponse: response.data,
        headers: response.headers,
        config: {
          url: response.config.url,
          method: response.config.method,
          headers: response.config.headers,
        },
      });
    }

    return response;
  },
  async (error) => {
    const timestamp = new Date().toISOString();

    console.error("❌ API Error:", {
      method: error.config?.method?.toUpperCase(),
      url: error.config?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: error.response?.data,
      message: error.message,
      timestamp,
    });

    if (error.response) {
      const { status, data, config } = error.response;

      console.error("❌ API Error Details:", {
        status,
        data,
        requestConfig: {
          url: config?.url,
          method: config?.method,
          headers: config?.headers,
          data: config?.data,
        },
      });

      // Authentication errors
      if (status === 401) {
        logError("Authentication failed", { url: error.config?.url, data });
        console.error("❌ API Error: Authentication failed");
        // Only clear token and redirect if not on auth-related pages
        const currentPath = window.location.pathname;
        if (
          !currentPath.includes("/login") &&
          !currentPath.includes("/register")
        ) {
          localStorage.removeItem("auth_token");
          localStorage.removeItem("user");

          // Don't redirect during tests or if explicitly disabled
          if (process.env.NODE_ENV !== "test" && !config?.skipAuthRedirect) {
            window.location.href = "/login";
          }
        }
      } else if (status === 403) {
        logError("Access forbidden", { url: error.config?.url, data });
        console.error("❌ API Error: Access forbidden");
      } else if (status === 402) {
        logError("Payment Required", { url: error.config?.url, data });
        console.error("❌ API Error: Payment required");
      } else if (status === 429) {
        logError("Rate limit exceeded", { url: error.config?.url, data });
        console.error("❌ API Error: Rate limit exceeded, retrying with backoff");
        
        // Special handling for rate limits
        if (!config._isRetry) {
          config._isRetry = true;
          const retryDelay = parseInt(error.response.headers['retry-after'] || '2', 10) * 1000;
          console.log(`🔄 API Retry: Waiting ${retryDelay}ms before retrying due to rate limit`);
          try {
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            return await api(config);
          } catch (retryError) {
            console.error("❌ API Retry: Failed after rate limit retry", retryError);
            return Promise.reject(retryError);
          }
        }
      } else if (status >= 500) {
        logError("Server error occurred", {
          status,
          url: error.config?.url,
          data,
        });
        console.error("❌ API Error: Server error", { status, data });

        // For 5xx errors, retry the request
        if (!config._isRetry) {
          config._isRetry = true;
          console.log("🔄 API Retry: Retrying request after server error");
          try {
            return await retryRequest(() => api(config));
          } catch (retryError) {
            console.error("❌ API Retry: Failed after retry", retryError);
            return Promise.reject(retryError);
          }
        }
      }
    } else if (error.request) {
      // Network errors (no response)
      logError("No response received from API", { url: error.config?.url });
      console.error("❌ API Error: No response received (network error)");

      // Retry network errors for all methods
      if (!error.config?._isRetry) {
        const config = error.config;
        config._isRetry = true;

        console.log("🔄 API Retry: Retrying request after network error");
        try {
          return await retryRequest(() => api(config));
        } catch (retryError) {
          console.error("❌ API Retry: Failed after retry", retryError);
          return Promise.reject(retryError);
        }
      }
    } else {
      logError("Error setting up API request", { message: error.message });
      console.error("❌ API Error: Request setup error", error.message);
    }

    return Promise.reject(error);
  }
);

// Campaign API
export const campaignApi = {
  async list(): Promise<Campaign[]> {
    try {
      const response = await api.get("/campaigns");
      if (response.data && Array.isArray(response.data)) {
        return response.data;
      } else if (response.data.success) {
        return response.data.campaigns;
      }
      throw new Error(
        response.data.error ||
          "Failed to fetch campaigns - Unexpected response format"
      );
    } catch (error) {
      logError("Error fetching campaigns", { error });
      throw error;
    }
  },

  async get(id: string): Promise<Campaign> {
    try {
      const response = await api.get(`/campaigns/${id}`);
      if (response.data) {
        return response.data;
      }
      throw new Error(
        response.data.error ||
          "Failed to fetch campaign - Unexpected response format"
      );
    } catch (error) {
      logError(`Error fetching campaign ${id}`, { error });
      throw error;
    }
  },

  async create(campaign: Partial<EnhancedCampaign>): Promise<Campaign> {
    try {
      const response = await api.post("/campaigns", campaign);
      if (response.data) {
        return response.data;
      }
      throw new Error(
        response.data.error ||
          "Failed to create campaign - Unexpected response format"
      );
    } catch (error) {
      logError("Error creating campaign", { error, campaign });
      throw error;
    }
  },

  async update(
    id: string,
    updates: Partial<EnhancedCampaign>
  ): Promise<Campaign> {
    try {
      const response = await api.put(`/campaigns/${id}`, updates);
      if (response.data) {
        return response.data;
      }
      throw new Error(
        response.data.error ||
          "Failed to update campaign - Unexpected response format"
      );
    } catch (error) {
      logError(`Error updating campaign ${id}`, { error, updates });
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const response = await api.delete(`/campaigns/${id}`);
      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to delete campaign");
      }
    } catch (error) {
      logError(`Error deleting campaign ${id}`, { error });
      throw error;
    }
  },

  async execute(id: string): Promise<void> {
    try {
      const response = await api.post(`/campaigns/${id}/execute`, {});
      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to execute campaign");
      }
    } catch (error) {
      logError(`Error executing campaign ${id}`, { error });
      throw new Error(
        "There was a problem launching your campaign. Please try again."
      );
    }
  },

  async cancel(id: string): Promise<void> {
    try {
      const response = await api.post(`/campaigns/${id}/cancel`, {});
      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to cancel campaign");
      }
    } catch (error) {
      logError(`Error canceling campaign ${id}`, { error });
      throw error;
    }
  },

  async getStats(clientId?: string): Promise<CampaignStats> {
    try {
      const params = clientId ? { clientId } : {};
      const response = await api.get("/stats/campaigns", { params });
      if (response.data) {
        return response.data;
      }
      throw new Error(
        response.data.error ||
          "Failed to fetch campaign stats - Unexpected response format"
      );
    } catch (error) {
      logError("Error fetching campaign stats", { error, clientId });
      throw error;
    }
  },
};

// Lead API
export const leadApi = {
  async list(): Promise<any[]> {
    try {
      const response = await api.get("/leads");
      return response.data;
    } catch (error) {
      logError("Error fetching leads", { error });
      throw error;
    }
  },

  async get(id: string): Promise<any> {
    try {
      const response = await api.get(`/leads/${id}`);
      return response.data;
    } catch (error) {
      logError(`Error fetching lead ${id}`, { error });
      throw error;
    }
  },

  async update(id: string, data: any): Promise<any> {
    try {
      const response = await api.put(`/leads/${id}`, data);
      return response.data;
    } catch (error) {
      logError(`Error updating lead ${id}`, { error });
      throw error;
    }
  },
};

// Product API
export const productApi = {
  async list(): Promise<any[]> {
    try {
      const response = await api.get("/products");
      return response.data;
    } catch (error) {
      logError("Error fetching products", { error });
      throw error;
    }
  },

  async get(id: string): Promise<any> {
    try {
      const response = await api.get(`/products/${id}`);
      return response.data;
    } catch (error) {
      logError(`Error fetching product ${id}`, { error });
      throw error;
    }
  },

  async create(data: any): Promise<any> {
    try {
      const response = await api.post("/products", data);
      return response.data;
    } catch (error) {
      logError("Error creating product", { error });
      throw error;
    }
  },

  async update(id: string, data: any): Promise<any> {
    try {
      const response = await api.put(`/products/${id}`, data);
      return response.data;
    } catch (error) {
      logError(`Error updating product ${id}`, { error });
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      await api.delete(`/products/${id}`);
    } catch (error) {
      logError(`Error deleting product ${id}`, { error });
      throw error;
    }
  },
};

// Launch API
export const launchApi = {
  async launchCampaign(data: any): Promise<any> {
    try {
      const response = await api.post("/launch", data);
      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to launch campaign");
      }
      return response.data.campaign;
    } catch (error) {
      logError("Error launching campaign:", { error });
      throw new Error(
        "There was a problem launching your campaign. Please try again."
      );
    }
  },

  async generateContent(prompt: string): Promise<any> {
    try {
      const response = await api.post("/launch/generate", { prompt });
      if (!response.data) {
        throw new Error("Failed to generate content - No response data");
      }
      return response.data;
    } catch (error) {
      logError("Error generating content:", { error });
      throw new Error("Failed to generate content. Please try again.");
    }
  },
};

// Billing API
export const billingApi = {
  getUsage: () => Promise.resolve({ data: { credits: 1000, used: 100 } }),
  getSubscription: () =>
    Promise.resolve({ data: { plan: "pro", status: "active" } }),
  updateSubscription: (data: any) =>
    Promise.resolve({ data: { ...data, status: "active" } }),
};

// Support API
export const supportApi = {
  createTicket: (data: any) =>
    Promise.resolve({ data: { ...data, id: Date.now().toString() } }),
  getTickets: () => Promise.resolve({ data: [] }),
};

export default api;
